import { useLingui } from '@lingui/react/macro';

import { useZuubRcmOnboardingStatus } from '@/react-query/rcm/useZuubRcmOnboarding';

interface UseZuubOnboardingConnectionStatusProps {
  payerId: number;
  isTestingConnection: boolean;
  isLocalTestingConnection: boolean;
  isLoadingOnboarding: boolean;
  hasError: boolean;
}

export const useZuubOnboardingConnectionStatus = ({
  payerId,
  isTestingConnection,
  isLocalTestingConnection,
  isLoadingOnboarding,
  hasError,
}: UseZuubOnboardingConnectionStatusProps) => {
  const { t } = useLingui();

  // Determine whether polling should be enabled
  const isTestingEnabled = isTestingConnection || isLocalTestingConnection;

  // Status check with polling
  const {
    data: statusData,
    isLoading: isStatusLoading,
    refetch: refetchStatus,
  } = useZuubRcmOnboardingStatus(payerId, isTestingEnabled);

  // Computed status values
  const isFailedStatus = statusData?.credentialStatus === 'Failed';
  const isSucceededStatus = statusData?.credentialStatus === 'Succeeded';

  // StatusChip props
  const getStatusChipProps = () => {
    const status = statusData?.credentialStatus;
    if (status === 'Succeeded') {
      return { text: t`Connected`, variant: 'success' as const };
    }
    if (status === 'Failed') {
      return { text: t`Set Up Error`, variant: 'warning' as const };
    }
    // Loading/polling state: treat as connecting (but only if not in failed state)
    if ((isTestingEnabled && isStatusLoading) || isLocalTestingConnection) {
      return { text: t`Connecting`, variant: 'progress' as const };
    }
    if (status === 'Acknowledged' || status === 'InProgress') {
      return { text: t`Connecting`, variant: 'progress' as const };
    }
    // Default state (including general error)
    return { text: t`Not connected`, variant: 'default' as const };
  };

  // Banner configuration
  const getBannerConfig = () => {
    if (hasError) {
      return {
        title: t`Contact Videa Support`,
        description: t`Something went wrong on our end. Please contact customer support so we can help you get set up.`,
        variant: 'error' as const,
        showFooter: true,
        footerText: t`Contact customer support →`,
        footerAction: () => {
          window.location.href = 'mailto:support@videa.ai';
        },
      };
    }

    if (isFailedStatus) {
      return {
        title: t`Login credentials failed`,
        description: t`We were unable to sign into the payer portal using the credentials you entered here. Please verify the credentials and update them here.`,
        variant: 'warning' as const,
        showFooter: false,
      };
    }

    if (isTestingEnabled) {
      return {
        title: t`Testing the connection`,
        description: t`We're testing the credentials you've provided to make sure everything is working.`,
        variant: 'progress' as const,
        showFooter: true,
        footerText: t`Please stay on this page while the check runs.`,
        footerAction: null,
      };
    }

    if (isLoadingOnboarding) {
      return {
        title: t`Generating login credentials...`,
        description: t`Please wait while we prepare your connection to the payer portal.`,
        variant: 'progress' as const,
        showFooter: false,
      };
    }

    return {
      title: t`Connect Videa to the payer portal to access complete benefit information`,
      description: t`Certain benefit information is only available by logging into the payer portal. Follow the instructions below to complete the connection process.`,
      variant: 'primary' as const,
      showFooter: false,
    };
  };

  // Button configuration
  const getButtonConfig = () => {
    // When succeeded, button should be enabled and say "Back to Patient List"
    if (isSucceededStatus) {
      return {
        disabled: false,
        text: t`Back to Patient List`,
      };
    }

    const isDisabled =
      hasError ||
      isLoadingOnboarding ||
      ((isTestingConnection || isLocalTestingConnection) && !isFailedStatus);
    const text =
      (isTestingConnection || isLocalTestingConnection) && !isFailedStatus
        ? t`Testing...`
        : t`Test Connection`;

    return {
      disabled: isDisabled,
      text,
    };
  };

  // Show banner condition
  const shouldShowBanner = !isSucceededStatus;

  return {
    // Status data
    statusData,
    isStatusLoading,
    isFailedStatus,
    isSucceededStatus,
    refetchStatus,

    // UI configurations
    statusChipProps: getStatusChipProps(),
    bannerConfig: getBannerConfig(),
    buttonConfig: getButtonConfig(),
    shouldShowBanner,
  };
};
